// garuda - Activities (The Pulse) Page

import React, { useState, useEffect } from "react";
import { useNavigation } from "../context/NavigationContext";
import { ParenthesesBg } from "../components/Icons";
import { fetchActivities } from "../services/contentService";
import JackpotWord from "../components/JackpotWord";
import PageNavigation from "../components/PageNavigation";
import "./Activities.css";

const Activities = () => {
  const { navigateTo } = useNavigation();
  const [activities, setActivities] = useState([]);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [heatmapData, setHeatmapData] = useState([]);
  const [allHeatmapData, setAllHeatmapData] = useState({});
  const [currentYear] = useState(2026);

  // Load data from content service on mount
  useEffect(() => {
    const loadActivities = async () => {
      const data = await fetchActivities();
      setActivities(data.activities);
      setAllHeatmapData(data.heatmapData);
    };
    loadActivities();
  }, []);

  const handleActivityClick = (activity) => {
    setSelectedActivity(activity);
    // Load heatmap data for this activity (array of date strings)
    const activityData = allHeatmapData[activity.id] || [];
    setHeatmapData(activityData);
  };

  const handleBack = () => {
    setSelectedActivity(null);
  };

  // Toggle disabled - data is read-only from manifest

  const generateHeatmap = () => {
    const months = [];

    // Generate data for each month
    for (let month = 0; month < 12; month++) {
      const monthWeeks = [];
      const firstDay = new Date(currentYear, month, 1);
      const lastDay = new Date(currentYear, month + 1, 0);

      // Start from the first Sunday of the week containing the 1st
      const firstSunday = new Date(firstDay);
      firstSunday.setDate(firstDay.getDate() - firstDay.getDay());

      let currentDate = new Date(firstSunday);
      let week = [];
      let iterations = 0;
      const maxIterations = 50; // Safety limit per month

      while (currentDate <= lastDay && iterations < maxIterations) {
        if (week.length === 7) {
          monthWeeks.push(week);
          week = [];
        }

        const year = currentDate.getFullYear();
        const monthStr = String(currentDate.getMonth() + 1).padStart(2, "0");
        const day = String(currentDate.getDate()).padStart(2, "0");
        const dateStr = `${year}-${monthStr}-${day}`;
        const hasData = heatmapData.includes(dateStr);
        const isCurrentMonth = currentDate.getMonth() === month;

        week.push({
          date: dateStr,
          active: hasData,
          inMonth: isCurrentMonth,
        });

        currentDate.setDate(currentDate.getDate() + 1);
        iterations++;
      }

      // Push remaining days
      if (week.length > 0) {
        while (week.length < 7) {
          week.push({ date: "", active: false, inMonth: false });
        }
        monthWeeks.push(week);
      }

      months.push({
        month: month,
        weeks: monthWeeks,
      });
    }

    return months;
  };

  if (selectedActivity) {
    const months = generateHeatmap();
    const totalDays = heatmapData.length;

    return (
      <div className="activities-container heatmap-view">
        <PageNavigation />
        <ParenthesesBg opacity={0.03} />

        <div className="heatmap-header">
          <button className="back-button" onClick={handleBack}>
            ← Back to Activities
          </button>
          <div className="heatmap-title-section">
            <h1 className="heatmap-title">{selectedActivity.name}</h1>
            <p className="heatmap-stats">
              {totalDays} days tracked in {currentYear}
            </p>
            <p className="heatmap-start-date">
              Tracking started: 28th Feb, 2026
            </p>
          </div>
        </div>

        <div className="heatmap-container">
          <div className="heatmap-grid">
            <div className="heatmap-days-labels">
              <span>Sun</span>
              <span>Mon</span>
              <span>Tue</span>
              <span>Wed</span>
              <span>Thu</span>
              <span>Fri</span>
              <span>Sat</span>
            </div>
            <div className="heatmap-months-grid">
              {months.map((monthData, monthIndex) => (
                <div key={monthIndex} className="heatmap-month-section">
                  <div className="heatmap-month-label">
                    {
                      [
                        "Jan",
                        "Feb",
                        "Mar",
                        "Apr",
                        "May",
                        "Jun",
                        "Jul",
                        "Aug",
                        "Sep",
                        "Oct",
                        "Nov",
                        "Dec",
                      ][monthData.month]
                    }
                  </div>
                  <div className="heatmap-weeks">
                    {monthData.weeks.map((week, weekIndex) => (
                      <div key={weekIndex} className="heatmap-week">
                        {week.map((day, dayIndex) => (
                          <div
                            key={dayIndex}
                            className={`heatmap-day ${day.active ? "active" : ""} ${!day.inMonth ? "inactive" : ""}`}
                            title={day.date}
                          />
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="activities-container">
      <PageNavigation />
      <ParenthesesBg opacity={0.05} />

      <div className="activities-header">
        <h1 className="page-title">
          <JackpotWord onClick={() => navigateTo("home")}>
            Activities
          </JackpotWord>
        </h1>
        <p className="page-subtitle">The pulse of daily rituals.</p>
      </div>

      <div className="activities-list">
        {activities.length === 0 ? (
          <p className="no-results">No activities tracked yet.</p>
        ) : (
          activities.map((activity) => (
            <div
              key={activity.id}
              className="activity-item"
              onClick={() => handleActivityClick(activity)}
            >
              <h2 className="activity-name">
                <JackpotWord>{activity.name}</JackpotWord>
              </h2>
              <span className="activity-arrow">→</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Activities;
